import React, { useMemo } from 'react';
import StatTile from '../ui/StatTile';
import SortableTable from '../SortableTable';
import AnnualProgressBar from '../ui/AnnualProgressBar';
import { format, differenceInDays, startOfYear, endOfYear } from 'date-fns';
import useVisitsTargetsAnalysis from '../../hooks/useVisitsTargetsAnalysis';
import useAnnualProgress from '../../hooks/useAnnualProgress';
import { isCompletedVisit } from '../../constants/visitsClassification';
import { countStoreVisits } from '../../constants/visitsUnivers';

const VisitsTargets = ({ visits, startDate, endDate, selectedProject, selectedShopSigns, fetchWithAuth }) => {
  const originalEndDate = new Date(endDate);

  // Utiliser le hook personnalisé pour la logique d'analyse
  const { targetAnalysis, loading, filteredTargets, periodDays, targets, adjustedEndDate } = useVisitsTargetsAnalysis({
    visits,
    startDate,
    endDate,
    selectedProject,
    selectedShopSigns,
    fetchWithAuth
  });

  // Utiliser le hook pour l'avancement annuel
  const { annualProgressData, loading: annualProgressLoading } = useAnnualProgress({
    selectedProject,
    startDate,
    endDate,
    fetchWithAuth
  });

  // Ajouter le useMemo pour vérifier si nous avons des données de durée à afficher
  const hasDurationTargets = useMemo(() => {
    if (!targetAnalysis) return false;

    // Vérifier si nous avons au moins une cible de durée définie
    const hasDurationData = targetAnalysis.storesUnderTargetDuration.length > 0 ||
                             targetAnalysis.storesOverTargetDuration.length > 0;

    // Vérifier si au moins un target a une durée définie
    const hasAnyTargetWithDuration = filteredTargets.some(target =>
      target.visit_duration && target.visit_duration > 0
    );

    return hasDurationData && hasAnyTargetWithDuration;
  }, [targetAnalysis, filteredTargets]);

  // Calcul de l'avancement annuel pour chaque magasin
  const annualProgressAnalysis = useMemo(() => {
    if (!filteredTargets || filteredTargets.length === 0 || annualProgressLoading) {
      return [];
    }

    const now = new Date();
    const currentYear = now.getFullYear();
    const yearStart = startOfYear(new Date(currentYear, 0, 1));
    const yearEnd = endOfYear(new Date(currentYear, 0, 1));

    // Jours écoulés depuis le début de l'année
    const daysPassed = differenceInDays(now, yearStart) + 1;
    const totalDaysInYear = differenceInDays(yearEnd, yearStart) + 1;

    return filteredTargets.map(target => {
      const storeName = target.intervention_point_name;

      // Utiliser les données du nouvel endpoint si disponibles
      let completedVisitsCount = 0;
      if (annualProgressData && annualProgressData.yearToDateCounts) {
        completedVisitsCount = annualProgressData.yearToDateCounts[storeName] || 0;
      } else {
        // Fallback sur les données existantes. countStoreVisits, pas .length : la table
        // est grainée par (visite × univers) et l'objectif est par magasin.
        completedVisitsCount = countStoreVisits(visits.filter(v =>
          isCompletedVisit(v) && v.interventionPoint === storeName
        ));
      }

      const annualTarget = target.visit_frequency;
      const expectedVisitsToDate = (annualTarget * daysPassed) / totalDaysInYear;
      const remainingVisits = Math.max(0, annualTarget - completedVisitsCount);

      // Calculer l'avance/retard
      const progressDifference = completedVisitsCount - expectedVisitsToDate;
      const progressPercentage = (completedVisitsCount / annualTarget) * 100;

      // Déterminer le statut et la couleur
      let status, color, message;
      if (progressDifference >= 1) {
        status = 'ahead';
        color = 'text-green-600 bg-green-50';
        message = `En avance de ${Math.round(progressDifference)} visite${Math.round(progressDifference) > 1 ? 's' : ''}`;
      } else if (progressDifference <= -1) {
        status = 'behind';
        color = 'text-red-600 bg-red-50';
        message = `En retard de ${Math.round(Math.abs(progressDifference))} visite${Math.round(Math.abs(progressDifference)) > 1 ? 's' : ''}`;
      } else {
        status = 'on-track';
        color = 'text-green-600 bg-green-50';
        message = 'Dans les temps';
      }

      // Trouver le secteur du magasin à partir des visites
      const storeVisits = visits.filter(v => v.interventionPoint === storeName);
      const sectorName = storeVisits.length > 0 ? storeVisits[0].sectorName : null;

      return {
        storeName,
        sectorName: sectorName || 'Non défini',
        shopSign: storeVisits.length > 0 ? storeVisits[0].shopSign : null,
        hasTarget: true,
        status,
        color,
        message,
        remainingVisits,
        totalVisitsIncludingPlanned: completedVisitsCount,
        annualTarget,
        progressPercentage: Math.round(progressPercentage),
        expectedVisitsToDate: Math.round(expectedVisitsToDate * 10) / 10,
        progressDifference: Math.round(progressDifference * 10) / 10,
        progressData: {
          hasTarget: true,
          status,
          color,
          message,
          remainingVisits,
          totalVisitsIncludingPlanned: completedVisitsCount,
          annualTarget,
          progressPercentage: Math.round(progressPercentage),
          expectedVisitsToDate: Math.round(expectedVisitsToDate * 10) / 10,
          progressDifference: Math.round(progressDifference * 10) / 10
        }
      };
    }).sort((a, b) => a.progressDifference - b.progressDifference); // Trier par écart, les en retard en premier
  }, [filteredTargets, visits, annualProgressData, annualProgressLoading]);


  if (loading)
    return <div className="p-4 flex justify-center text-gray-700 dark:text-gray-300">Chargement des objectifs de visite...</div>;

  if (!targets || targets.length === 0) {
    return (
      <div className="p-4 bg-yellow-50 border border-yellow-100 rounded-lg">
        <p className="text-yellow-700">Aucun objectif de visite défini pour ce projet.</p>
      </div>
    );
  }

  if (!targetAnalysis) {
    return (
      <div className="p-4 bg-gray-50 border border-gray-100 rounded-lg">
        <p className="text-gray-700">Analyse des objectifs en cours...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="mb-4 bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-100 dark:border-blue-800">
        <h3 className="text-blue-700 dark:text-blue-300 font-medium mb-1">Analyse des objectifs de visite pour la période du {format(new Date(startDate), 'dd/MM/yyyy')} au {format(new Date(adjustedEndDate), 'dd/MM/yyyy')} ({periodDays} jours)</h3>
        <p className="text-sm text-blue-600 dark:text-blue-400">
          Les objectifs de fréquence de visite sont exprimés en nombre de visites par an.
          L'analyse calcule un prorata sur la période sélectionnée pour déterminer le nombre de visites attendues.
          Par exemple, pour un objectif de 4 visites/an, on attendra 1 visite sur un trimestre.
          {format(originalEndDate, 'yyyy-MM-dd') !== format(adjustedEndDate, 'yyyy-MM-dd') && (
            <span className="text-orange-500 flex items-center">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              &nbsp;La date de fin de la période sélectionnée a été automatiquement modifiée à la date du jour afin de calculer au mieux le prorata des objectifs.
            </span>
          )}
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <StatTile
          label="Points de vente avec objectif"
          value={targetAnalysis.totalTargets}
          icon={
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
          }
          color="blue"
        />

        <StatTile
          label="Magasins non visités"
          value={targetAnalysis.storesWithTargetsNoVisitsCount}
          subValue={`${Math.round((targetAnalysis.storesWithTargetsNoVisitsCount / targetAnalysis.totalTargets) * 100)}%`}
          icon={
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          }
          color="red"
        />

        <StatTile
          label="Magasins hors objectif"
          value={targetAnalysis.storesWithVisitsNoTargetsCount}
          subValue={`${Math.round((targetAnalysis.storesWithVisitsNoTargetsCount / (targetAnalysis.totalTargets || 1)) * 100)}%`}
          icon={
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          }
          color="gray"
        />
      </div>

      {/* Tableau d'avancement annuel */}
      {annualProgressAnalysis.length > 0 && !annualProgressLoading && (
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4">
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
            Avancement des objectifs annuels
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            Suivi de l'avancement de chaque magasin par rapport à son objectif annuel de visites.
          </p>
          <SortableTable
            data={annualProgressAnalysis}
            columns={[
              { key: 'storeName', label: 'Point de vente' },
              { key: 'sectorName', label: 'Secteur' },
              {
                key: 'annualProgress',
                label: 'Avancement Objectif Annuel',
                render: (item) => (
                  <AnnualProgressBar
                    progressData={item.progressData}
                    showLegend={false}
                    size="small"
                  />
                ),
                rawValue: (item) => {
                  if (item.progressDifference <= -1) {
                    return 'En retard';
                  } else if (item.progressDifference >= 1 && item.progressPercentage <= 100) {
                    return 'Avance';
                  } else if (item.progressDifference >= 1 && item.progressPercentage > 100) {
                    return 'Dépassé';
                  } else {
                    return 'Conforme';
                  }
                },
                sortFunction: (a, b, direction) => {
                  return direction === 'ascending' ? a.progressPercentage - b.progressPercentage : b.progressPercentage - a.progressPercentage;
                }
              },
            ]}
            emptyMessage="Aucune donnée d'avancement annuel disponible"
            initialSortBy={{ key: 'annualProgress', direction: 'ascending' }}
            exportable={true}
            exportFilename="avancement_objectifs_annuels.csv"
          />
        </div>
      )}

      <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4">
        <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Fréquence de visite</h3>

        <div className="flex justify-between mb-4 gap-4">
          <StatTile
            label="Sous-visite"
            value={targetAnalysis.storesUnderTargetFrequencyCount}
            subValue={`${Math.round((targetAnalysis.storesUnderTargetFrequencyCount / (targetAnalysis.storesUnderTargetFrequencyCount + targetAnalysis.storesConformTargetFrequencyCount + targetAnalysis.storesOverTargetFrequencyCount || 1)) * 100)}%`}
            icon={
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 17h8m0 0V9m0 8l-8-8-4 4-6-6" />
              </svg>
            }
            color="red"
            size="small"
          />

          <StatTile
            label="Conforme"
            value={targetAnalysis.storesConformTargetFrequencyCount}
            subValue={`${Math.round((targetAnalysis.storesConformTargetFrequencyCount / (targetAnalysis.storesUnderTargetFrequencyCount + targetAnalysis.storesConformTargetFrequencyCount + targetAnalysis.storesOverTargetFrequencyCount || 1)) * 100)}%`}
            icon={
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            }
            color="green"
            size="small"
          />

          <StatTile
            label="Sur-visite"
            value={targetAnalysis.storesOverTargetFrequencyCount}
            subValue={`${Math.round((targetAnalysis.storesOverTargetFrequencyCount / (targetAnalysis.storesUnderTargetFrequencyCount + targetAnalysis.storesConformTargetFrequencyCount + targetAnalysis.storesOverTargetFrequencyCount || 1)) * 100)}%`}
            icon={
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
              </svg>
            }
            color="orange"
            size="small"
          />
        </div>

        <div className="mt-4">
          <h4 className="font-medium text-sm text-gray-700 dark:text-gray-300 mb-2">Comparaison fréquence des visites :</h4>
          <SortableTable
            data={[
              ...targetAnalysis.storesUnderTargetFrequency,
              ...targetAnalysis.storesConformTargetFrequency,
              ...targetAnalysis.storesOverTargetFrequency
            ]}
            columns={[
              { key: 'storeName', label: 'Point de vente' },
              { key: 'sectorName', label: 'Secteur', render: (item) => item.sectorName || '-' },
              {
                key: 'targetFrequency',
                label: 'Fréquence cible (visites/an)',
                render: (item) => item.targetFrequency?.toFixed(1) || '-'
              },
              {
                key: 'expectedVisitsInPeriod',
                label: 'Visites attendues',
                render: (item) => item.expectedVisitsInPeriod?.toFixed(1) || '-'
              },
              { key: 'actualVisits', label: 'Visites réelles' },
              {
                key: 'difference',
                label: 'Écart',
                render: (item) => {
                  const diff = item.difference?.toFixed(1) || '0';
                  const colorClass = item.frequencyStatus === 'Conforme' ? 'text-green-600' :
                                   item.frequencyStatus === 'Sous-visite' ? 'text-red-600' : 'text-orange-600';
                  return <span className={colorClass}>{diff > 0 ? '+' : ''}{diff}</span>;
                }
              },
              {
                key: 'frequencyStatus',
                label: 'Statut',
                render: (item) => {
                  const colorClass = item.frequencyStatus === 'Conforme' ? 'text-green-600' :
                                   item.frequencyStatus === 'Sous-visite' ? 'text-red-600' : 'text-orange-600';
                  return <span className={colorClass}>{item.frequencyStatus}</span>;
                }
              }
            ]}
            emptyMessage="Aucun point de vente avec objectifs"
            initialSortBy={{ key: 'difference', direction: 'ascending' }}
            exportable={true}
            exportFilename="comparaison_frequence_visites.csv"
          />
        </div>
      </div>



      {/* Section durée - uniquement si des données de durée existent */}
      {hasDurationTargets && (
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4">
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Durée de visite</h3>

          <div className="flex justify-between mb-4 gap-4">
            <StatTile
              label="Sous-durée"
              value={targetAnalysis.storesUnderTargetDurationCount}
              subValue={`${Math.round((targetAnalysis.storesUnderTargetDurationCount / (targetAnalysis.storesUnderTargetDurationCount + targetAnalysis.storesConformTargetDurationCount + targetAnalysis.storesOverTargetDurationCount || 1)) * 100)}%`}
              icon={
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              }
              color="red"
              size="small"
            />

            <StatTile
              label="Conforme"
              value={targetAnalysis.storesConformTargetDurationCount}
              subValue={`${Math.round((targetAnalysis.storesConformTargetDurationCount / (targetAnalysis.storesUnderTargetDurationCount + targetAnalysis.storesConformTargetDurationCount + targetAnalysis.storesOverTargetDurationCount || 1)) * 100)}%`}
              icon={
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              }
              color="green"
              size="small"
            />

            <StatTile
              label="Sur-durée"
              value={targetAnalysis.storesOverTargetDurationCount}
              subValue={`${Math.round((targetAnalysis.storesOverTargetDurationCount / (targetAnalysis.storesUnderTargetDurationCount + targetAnalysis.storesConformTargetDurationCount + targetAnalysis.storesOverTargetDurationCount || 1)) * 100)}%`}
              icon={
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              }
              color="orange"
              size="small"
            />
          </div>

          <div className="mt-4">
            <h4 className="font-medium text-sm text-gray-700 dark:text-gray-300 mb-2">Comparaison durée des visites :</h4>
            <SortableTable
              data={[
                ...targetAnalysis.storesUnderTargetDuration,
                ...targetAnalysis.storesConformTargetDuration,
                ...targetAnalysis.storesOverTargetDuration
              ]}
              columns={[
                { key: 'storeName', label: 'Point de vente' },
                { key: 'sectorName', label: 'Secteur', render: (item) => item.sectorName || '-' },
                {
                  key: 'targetDuration',
                  label: 'Durée cible (min)',
                  render: (item) => item.targetDuration || '-'
                },
                {
                  key: 'avgDuration',
                  label: 'Durée moyenne (min)',
                  render: (item) => item.avgDuration || '-'
                },
                {
                  key: 'durationDifference',
                  label: 'Écart (min)',
                  render: (item) => {
                    const diff = item.durationDifference || 0;
                    const colorClass = item.durationStatus === 'Conforme' ? 'text-green-600' :
                                     item.durationStatus === 'Sous-durée' ? 'text-red-600' : 'text-orange-600';
                    return <span className={colorClass}>{diff > 0 ? '+' : ''}{diff}</span>;
                  }
                },
                {
                  key: 'durationDifferencePercentage',
                  label: 'Écart (%)',
                  render: (item) => `${item.durationDifferencePercentage?.toFixed(1) || '0'}%`
                },
                {
                  key: 'durationStatus',
                  label: 'Statut',
                  render: (item) => {
                    const colorClass = item.durationStatus === 'Conforme' ? 'text-green-600' :
                                     item.durationStatus === 'Sous-durée' ? 'text-red-600' : 'text-orange-600';
                    return <span className={colorClass}>{item.durationStatus}</span>;
                  }
                }
              ]}
              emptyMessage="Aucun point de vente avec objectifs de durée"
              initialSortBy={{ key: 'durationDifferencePercentage', direction: 'descending' }}
              exportable={true}
              exportFilename="comparaison_duree_visites.csv"
            />
          </div>
        </div>
      )}



      {/* Points de vente sans objectif */}
      {targetAnalysis.storesWithVisitsNoTargets.length > 0 && (
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4">
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
            Points de vente sans objectif
          </h3>
          <SortableTable
            data={targetAnalysis.storesWithVisitsNoTargets}
            columns={[
              { key: 'storeName', label: 'Point de vente' },
              { key: 'actualVisits', label: 'Visites' },
              {
                key: 'users',
                label: 'Utilisateur(s)',
                rawValue: (item) => item.primaryUser,
                render: (item) => {
                  if (!item.users || item.users.length === 0) return '-';

                  // Afficher tous les utilisateurs s'il y en a plusieurs
                  if (item.users.length > 1) {
                    return (
                      <div title={item.users.join(', ')}>
                        {item.primaryUser} +{item.users.length - 1}
                      </div>
                    );
                  }

                  return item.primaryUser;
                }
              },
              {
                key: 'avgDuration',
                label: 'Durée moyenne',
                render: (item) => `${item.avgDuration} min`
              }
            ]}
            emptyMessage="Aucun point de vente visité sans objectif défini"
            initialSortBy={{ key: 'actualVisits', direction: 'descending' }}
            exportable={true}
            exportFilename="magasins_sans_objectif.csv"
          />
        </div>
      )}

    </div>
  );
};

export default VisitsTargets;